import { datadogLogs } from "@datadog/browser-logs";
import { describeThrownValue } from "@/helpers/apiErrorShape";

/**
 * Structured error logger that forwards to Datadog Browser Logs in non-development
 * environments and to the browser console everywhere. Use this instead of bare
 * `console.error(err, { tags, extra })` — Datadog's SDK can render the typed `error`
 * argument as a stack trace and the `context` argument as searchable attributes,
 * but only when the call shape matches `logger.error(message, context, error)`.
 *
 * @param message - Human-readable description of what was happening when the error occurred
 * @param error - The underlying Error/value that was caught
 * @param context - Optional structured attributes (tags/extra/metadata) for filtering in Datadog
 */
export function logError(message: string, error: unknown, context?: Record<string, unknown>): void {
	// LSP-19423: `String(error)` collapsed every non-Error rejection to "[object Object]". This app's
	// API layer rejects a plain `{ code, message, httpStatus, ... }` object on every non-2xx response,
	// so that stringification was silently discarding the status and the server's own copy on the
	// majority of reported failures — the reported error named nothing that could be acted on.
	const errorInstance = error instanceof Error ? error : new Error(describeThrownValue(error));

	// Always emit to console so local dev and the browser inspector still see the error.
	// In production, Datadog's `forwardConsoleLogs: ["error"]` also picks this up,
	// but logger.error below gives Datadog a properly-typed Error and structured context.
	console.error(message, errorInstance, context ?? {});

	try {
		datadogLogs.logger.error(message, context ?? {}, errorInstance);
	} catch (datadogError) {
		// Datadog not initialized (development) or the SDK crashed. Surface the
		// SDK failure via console.warn so a broken monitoring pipeline is at least
		// detectable in the browser inspector — otherwise an SDK crash loop would
		// silently degrade all structured error logging with no signal.
		console.warn("logError: Datadog Browser Logs SDK call failed", datadogError);
	}
}

/**
 * Structured info-level breadcrumb logger for non-error events that still need
 * to reach Datadog Browser Logs. Use for diagnostics where `console.debug` is
 * not visible (Datadog `forwardConsoleLogs: ["error"]` drops non-error console
 * calls; debug-level is hidden in the browser console by default) but the
 * event is not an actual failure — e.g. silent short-circuits, fallback paths,
 * permission denials.
 */
export function logInfo(message: string, context?: Record<string, unknown>): void {
	console.info(message, context ?? {});

	try {
		datadogLogs.logger.info(message, context ?? {});
	} catch (datadogError) {
		console.warn("logInfo: Datadog Browser Logs SDK call failed", datadogError);
	}
}

/**
 * Warn-level structured log to Datadog Browser Logs (single app SDK instance).
 * Use for expected-but-noteworthy signals that are not errors (e.g. an upload chunk
 * dropped on a flaky link). Mirrors logInfo; kept here so callers never import a second
 * Datadog SDK facade (which would trigger "SDK is loaded more than once").
 */
export function logWarn(message: string, context?: Record<string, unknown>): void {
	console.warn(message, context ?? {});

	try {
		datadogLogs.logger.warn(message, context ?? {});
	} catch (datadogError) {
		console.warn("logWarn: Datadog Browser Logs SDK call failed", datadogError);
	}
}
